import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../../lib/api";
import { PageHeader, Card, Loading, inputCls, labelCls, btnPrimary, btnSecondary } from "../../components/admin/common";
import { Loader2, Upload, Trash2, Mail, Key, Copy, RefreshCw, Eye, EyeOff, Bell, BellOff, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sigUrl, setSigUrl] = useState("");
  const [sigBusy, setSigBusy] = useState(false);
  const sigRef = useRef();
  useEffect(() => {
    api.get("/settings").then((r) => { setS(r.data); setSigUrl(r.data.signature_url || ""); }).catch(() => setS({}));
  }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    // Reminder fields are owned by PaymentReminderCard; never overwrite them with this stale snapshot.
    const body = Object.fromEntries(Object.entries(s).filter(([k]) => !k.startsWith("reminder_")));
    try { await api.put("/settings", body); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const uploadSig = async (file) => {
    if (!file) return;
    setSigBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSigUrl(data.signature_url);
      toast.success("Signature saved — its background was removed automatically. It will appear on every invoice.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSigBusy(false);
  };

  const removeSig = async () => {
    setSigBusy(true);
    try {
      await api.delete("/settings/signature");
      setSigUrl("");
      toast.success("Signature removed");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSigBusy(false);
  };

  if (!s) return <Loading />;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Organization and support configuration." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
        <Card className="p-6 space-y-4">
          <h3 className="font-head font-semibold text-slate-900">Organization</h3>
          <div><label className={labelCls}>Organization Name</label><input className={inputCls} value={s.org_name || ""} onChange={(e) => set("org_name", e.target.value)} data-testid="set-org-name" /></div>
          <div><label className={labelCls}>Address</label><textarea rows={2} className={inputCls} value={s.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
          <div><label className={labelCls}>Default UPI ID</label><input className={inputCls} value={s.default_upi || ""} onChange={(e) => set("default_upi", e.target.value)} placeholder="gdiot@upi" /></div>
          <div><label className={labelCls}>Default Payee Name</label><input className={inputCls} value={s.default_payee || ""} onChange={(e) => set("default_payee", e.target.value)} /></div>
        </Card>
        <Card className="p-6 space-y-4">
          <h3 className="font-head font-semibold text-slate-900">Support</h3>
          <div><label className={labelCls}>Support Email</label><input className={inputCls} value={s.support_email || ""} onChange={(e) => set("support_email", e.target.value)} /></div>
          <div><label className={labelCls}>Support Phone</label><input className={inputCls} value={s.support_phone || ""} onChange={(e) => set("support_phone", e.target.value)} /></div>
          <div><label className={labelCls}>Support WhatsApp Number</label><input className={inputCls} value={s.support_whatsapp || ""} onChange={(e) => set("support_whatsapp", e.target.value)} placeholder="91XXXXXXXXXX" /></div>
        </Card>
        <Card className="p-6 space-y-4" data-testid="payment-notify-card">
          <h3 className="font-head font-semibold text-slate-900">Payment Notification Emails</h3>
          <p className="text-xs text-slate-500">When a payment is completed, a "New payment arrived" email (with total registrations and the form name) is sent to these addresses at the same time as the customer's confirmation. Add up to two.</p>
          <div><label className={labelCls}>Notification Email 1</label><input className={inputCls} type="email" value={(s.admin_notify_emails || [])[0] || ""} onChange={(e) => { const a = [...(s.admin_notify_emails || [])]; a[0] = e.target.value; set("admin_notify_emails", a); }} placeholder="team@gdiot.in" data-testid="notify-email-1" /></div>
          <div><label className={labelCls}>Notification Email 2 <span className="text-slate-400 font-normal">(optional)</span></label><input className={inputCls} type="email" value={(s.admin_notify_emails || [])[1] || ""} onChange={(e) => { const a = [...(s.admin_notify_emails || [])]; a[1] = e.target.value; set("admin_notify_emails", a); }} placeholder="accounts@gdiot.in" data-testid="notify-email-2" /></div>
        </Card>
        <Card className="p-6 space-y-4">
          <h3 className="font-head font-semibold text-slate-900">Invoice Signature</h3>
          <p className="text-xs text-slate-500">Upload a photo/scan of the authorized signature. The white background is removed automatically and it appears above the &ldquo;Authorized Signature&rdquo; line on every invoice.</p>
          {sigUrl ? (
            <div className="space-y-3">
              <div className="border border-slate-200 rounded-xl bg-white p-3 flex items-center justify-center" data-testid="signature-preview-box">
                <img src={sigUrl} alt="Invoice signature" className="h-16 w-auto object-contain" data-testid="signature-preview" />
              </div>
              <div className="flex gap-2">
                <button type="button" className={btnSecondary} onClick={() => sigRef.current.click()} disabled={sigBusy} data-testid="replace-signature-btn">{sigBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Replace</button>
                <button type="button" className={`${btnSecondary} text-rose-600 hover:bg-rose-50`} onClick={removeSig} disabled={sigBusy} data-testid="remove-signature-btn"><Trash2 className="w-4 h-4" /> Remove</button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => sigRef.current.click()} disabled={sigBusy} className="w-full h-28 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-colors" data-testid="upload-signature-btn">
              {sigBusy ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Upload className="w-6 h-6 mb-1" /><span className="text-sm">Upload signature image</span></>}
            </button>
          )}
          <input ref={sigRef} type="file" accept="image/*" hidden onChange={(e) => uploadSig(e.target.files[0])} data-testid="signature-file-input" />
        </Card>
        <WhatsAppIntegrationCard />
        <EmailIntegrationCard />
        <ExternalApiCard />
        <PaymentReminderCard />
      </div>
      <div className="mt-6"><button className={btnPrimary} onClick={save} disabled={saving} data-testid="save-settings-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Settings</button></div>
    </div>
  );
}

function EmailIntegrationCard() {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [testTo, setTestTo] = useState("");
  useEffect(() => { api.get("/email/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false })); }, []);
  const test = async () => {
    setTesting(true); setResult(null);
    try {
      const { data } = await api.post("/email/test-connection", { to: testTo.trim() || null });
      setResult(data);
      if (data.connected) toast.success("Email sent successfully — check the inbox");
      else toast.error("Email connection failed");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setTesting(false);
  };
  return (
    <Card className="p-6 space-y-4" data-testid="email-integration-card">
      <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" /> Email Integration</h3>
      <div className="text-sm space-y-1">
        <div className="flex justify-between"><span className="text-slate-500">Service</span><span className="text-slate-700">{status?.service || "—"}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Sender</span><span className="text-slate-700 truncate max-w-[220px]" data-testid="email-sender">{status?.sender || "—"}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">API Status</span>
          <span className={status?.configured ? "text-emerald-600 font-medium" : "text-rose-600 font-medium"} data-testid="email-api-status">{status ? (status.configured ? "Connected" : "Not Connected") : "…"}</span></div>
      </div>
      <div>
        <label className={labelCls}>Send test email to</label>
        <input className={inputCls} value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com (blank = sender inbox)" data-testid="email-test-to" />
      </div>
      <button type="button" className={btnSecondary} onClick={test} disabled={testing || !status?.configured} data-testid="email-test-btn">{testing ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Test Email Connection</button>
      {result && <div className={`text-xs ${result.connected ? "text-emerald-600" : "text-rose-600"}`} data-testid="email-test-result">{result.connected ? "Connected — " : "Failed — "}{result.detail}</div>}
      <p className="text-[11px] text-slate-400">Payment confirmation emails (with the PDF invoice) are sent automatically from this address once a payment is completed.</p>
    </Card>
  );
}

function WhatsAppIntegrationCard() {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { api.get("/whatsapp/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false })); }, []);
  const test = async () => {
    setTesting(true); setResult(null);
    try {
      const { data } = await api.post("/whatsapp/test-connection");
      setResult(data);
      if (data.connected) toast.success("WhatsApp API Connected Successfully");
      else toast.error("WhatsApp API Connection Failed");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setTesting(false);
  };
  return (
    <Card className="p-6 space-y-4" data-testid="whatsapp-integration-card">
      <h3 className="font-head font-semibold text-slate-900">WhatsApp Integration</h3>
      <div className="text-sm space-y-1">
        <div className="flex justify-between"><span className="text-slate-500">API Status</span>
          <span className={status?.configured ? "text-emerald-600 font-medium" : "text-rose-600 font-medium"} data-testid="wa-api-status">{status ? (status.configured ? "Connected" : "Not Connected") : "…"}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">WhatsApp Service</span><span className="text-slate-700">{status?.service || "—"}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Sender</span><span className="text-slate-700 truncate max-w-[200px]">{status?.endpoint || "—"}</span></div>
      </div>
      <button type="button" className={btnSecondary} onClick={test} disabled={testing || !status?.configured} data-testid="wa-test-connection-btn">{testing ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Test WhatsApp Connection</button>
      {result && <div className={`text-xs ${result.connected ? "text-emerald-600" : "text-rose-600"}`}>{result.connected ? "WhatsApp API Connected Successfully" : "WhatsApp API Connection Failed"} — {result.detail}</div>}
      <p className="text-[11px] text-slate-400">API credentials are stored securely on the server. Automatic messages are configured per payment page.</p>
    </Card>
  );
}


function ExternalApiCard() {
  const [apiKey, setApiKey] = useState("");
  const [hasKey, setHasKey] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [generating, setGenerating] = useState(false);
  const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
  const listUrl = `${baseUrl}/api/external/transactions`;

  useEffect(() => {
    api.get("/settings/api-key")
      .then(r => { setApiKey(r.data.api_key || ""); setHasKey(r.data.has_key); })
      .catch(() => {});
  }, []);

  const generate = async () => {
    if (hasKey && !window.confirm("Regenerate API key?\n\nYour existing integrations will stop working until you update them with the new key.")) return;
    setGenerating(true);
    try {
      const r = await api.post("/settings/generate-api-key");
      setApiKey(r.data.api_key);
      setHasKey(true);
      setRevealed(true);
      toast.success("API key generated — copy it now");
    } catch { toast.error("Failed to generate API key"); }
    finally { setGenerating(false); }
  };

  const copy = (val, label) => {
    navigator.clipboard.writeText(val);
    toast.success(`${label} copied`);
  };

  const Row = ({ label, value, mono = false, secret = false }) => (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500 shrink-0 w-36">{label}</span>
      <span className={`flex-1 text-sm truncate ${mono ? "font-mono text-slate-800" : "text-slate-700"}`}>
        {secret && !revealed ? "•".repeat(Math.min(value.length, 32)) : value || "—"}
      </span>
      {value && (
        <button onClick={() => copy(value, label)} className="shrink-0 p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600" title="Copy">
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <Key className="w-5 h-5 text-slate-600" />
        <h2 className="text-base font-semibold text-slate-800">External API</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Use these credentials to connect external tools (Zapier, Make, Google Apps Script, custom scripts) to pull payment data directly from GDIOT PAY.
      </p>

      <div className="bg-slate-50 rounded-xl border border-slate-200 px-4 mb-4">
        <Row label="API_LIST_URL"  value={listUrl}   mono />
        <Row label="API_AUTH_MODE" value="x-api-key" mono />
        <Row label="API_KEY"       value={apiKey}    mono secret />
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={generate}
          disabled={generating}
          data-testid="generate-api-key-btn"
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-900 disabled:opacity-50 transition-colors"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {hasKey ? "Regenerate Key" : "Generate API Key"}
        </button>
        {apiKey && (
          <button onClick={() => setRevealed(v => !v)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
            {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {revealed ? "Hide" : "Reveal"}
          </button>
        )}
      </div>

      {hasKey && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
          <strong>How to use:</strong> Send a GET request to <code className="bg-blue-100 px-1 rounded text-xs">{listUrl}</code> with header <code className="bg-blue-100 px-1 rounded text-xs">X-API-Key: your-key</code>.<br />
          Add <code className="bg-blue-100 px-1 rounded text-xs">?status=completed</code> (default), <code className="bg-blue-100 px-1 rounded text-xs">?status=pending</code>, or <code className="bg-blue-100 px-1 rounded text-xs">?status=all</code> to filter.
        </div>
      )}
    </Card>
  );
}


const STATUS_COLORS = {
  sent: "bg-emerald-100 text-emerald-700",
  suppressed: "bg-slate-100 text-slate-600",
  pending: "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  failed_final: "bg-rose-100 text-rose-700",
};

function PaymentReminderCard() {
  const [cfg, setCfg] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const d = r.data || {};
      setCfg({
        reminder_enabled: !!d.reminder_enabled,
        reminder_minutes: d.reminder_minutes || 5,
        reminder_template_name: d.reminder_template_name || "",
        reminder_language_code: d.reminder_language_code || "en",
        reminder_body_params: d.reminder_body_params || [],
        reminder_header_param: d.reminder_header_param || "",
        reminder_button_param: d.reminder_button_param || "",
      });
    }).catch(() => setCfg({}));
  }, []);

  const loadTemplates = async () => {
    setTplLoading(true);
    try {
      const { data } = await api.get("/whatsapp/templates");
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load WhatsApp templates");
    }
    setTplLoading(false);
  };

  useEffect(() => { loadTemplates(); }, []);

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const { data } = await api.get("/reminder/logs");
      setLogs(data || []);
    } catch { setLogs([]); }
    setLogsLoading(false);
  };

  const toggleLogs = () => {
    if (!showLogs) loadLogs();
    setShowLogs((v) => !v);
  };

  const selectedTpl = templates.find((t) => t.name === cfg?.reminder_template_name);
  const bodyCount = selectedTpl?.body_param_count || 0;

  const setParam = (i, v) => {
    const params = [...(cfg.reminder_body_params || [])];
    params[i] = v;
    setCfg((p) => ({ ...p, reminder_body_params: params }));
  };

  const [toggleSaving, setToggleSaving] = useState(false);

  const toggleEnabled = async () => {
    const newVal = !cfg.reminder_enabled;
    setCfg((p) => ({ ...p, reminder_enabled: newVal }));
    setToggleSaving(true);
    try {
      await api.put("/settings", { reminder_enabled: newVal });
      toast.success(newVal ? "Payment reminder enabled and saved" : "Payment reminder disabled and saved");
    } catch (e) {
      // Revert local state if save failed
      setCfg((p) => ({ ...p, reminder_enabled: !newVal }));
      toast.error(apiError(e.response?.data?.detail) || "Failed to save");
    }
    setToggleSaving(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", cfg);
      toast.success("Payment reminder settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  if (!cfg) return null;

  return (
    <Card className="p-6 space-y-4 lg:col-span-2" data-testid="payment-reminder-card">
      <div className="flex items-center justify-between">
        <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2">
          {cfg.reminder_enabled ? <Bell className="w-4 h-4 text-blue-600" /> : <BellOff className="w-4 h-4 text-slate-400" />}
          Payment Reminder (WhatsApp)
        </h3>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="reminder-enabled-toggle-label">
          <span className="text-sm text-slate-500">{cfg.reminder_enabled ? "On" : "Off"}</span>
          <button
            type="button"
            role="switch"
            aria-checked={cfg.reminder_enabled}
            data-testid="reminder-enabled-toggle"
            disabled={toggleSaving}
            onClick={toggleEnabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-70 ${cfg.reminder_enabled ? "bg-blue-600" : "bg-slate-200"}`}
          >
            {toggleSaving
              ? <span className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-3 h-3 animate-spin text-white" /></span>
              : <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${cfg.reminder_enabled ? "translate-x-6" : "translate-x-1"}`} />
            }
          </button>
        </label>
      </div>
      <p className="text-xs text-slate-500">
        When enabled, a WhatsApp reminder is sent to the parent if they click "I've paid" but do not upload a payment screenshot within the configured delay. The reminder is automatically cancelled once a screenshot is uploaded or payment is verified.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Reminder Delay</label>
          <select
            data-testid="reminder-minutes-select"
            className={inputCls}
            value={cfg.reminder_minutes}
            onChange={(e) => setCfg((p) => ({ ...p, reminder_minutes: Number(e.target.value) }))}
          >
            <option value={5}>5 minutes</option>
            <option value={10}>10 minutes</option>
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
          </select>
        </div>

        <div>
          <label className={labelCls}>WhatsApp Template</label>
          <div className="flex gap-2">
            <select
              data-testid="reminder-template-select"
              className={inputCls}
              value={cfg.reminder_template_name}
              onChange={(e) => {
                const name = e.target.value;
                const tpl = templates.find((t) => t.name === name);
                const lang = tpl?.language || "en";
                const count = tpl?.body_param_count || 0;
                setCfg((p) => ({
                  ...p,
                  reminder_template_name: name,
                  reminder_language_code: lang,
                  reminder_body_params: Array(count).fill(""),
                  reminder_header_param: "",
                  reminder_button_param: "",
                }));
              }}
            >
              <option value="">— select a template —</option>
              {templates.map((t) => (
                <option key={t.name} value={t.name}>{t.name}</option>
              ))}
            </select>
            <button type="button" onClick={loadTemplates} disabled={tplLoading} className={`${btnSecondary} px-2.5`} title="Refresh templates" data-testid="refresh-templates-btn">
              {tplLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          </div>
          {cfg.reminder_language_code && cfg.reminder_template_name && (
            <p className="text-xs text-slate-400 mt-1">Language: {cfg.reminder_language_code}</p>
          )}
        </div>
      </div>

      {selectedTpl && (
        <div className="space-y-3 border border-slate-200 rounded-xl p-4 bg-slate-50">
          <div className="text-sm font-medium text-slate-700">Template Message Preview</div>
          {selectedTpl.body_text && (
            <p className="text-xs text-slate-600 bg-white border border-slate-200 rounded-lg p-3 font-mono whitespace-pre-wrap">{selectedTpl.body_text}</p>
          )}
          {selectedTpl.header_has_var && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Header Variable</label>
              <input
                data-testid="reminder-header-param"
                className={inputCls}
                value={cfg.reminder_header_param || ""}
                placeholder="e.g. {{student_name}}"
                onChange={(e) => setCfg((p) => ({ ...p, reminder_header_param: e.target.value }))}
              />
            </div>
          )}
          {bodyCount > 0 && (
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-600">Body Variables ({bodyCount})</label>
              <p className="text-xs text-slate-400">Available: {`{{student_name}}`}, {`{{amount_due}}`}, {`{{payment_page_link}}`}, {`{{mobile}}`}, {`{{registration_id}}`}</p>
              {Array.from({ length: bodyCount }).map((_, i) => (
                <input
                  key={i}
                  data-testid={`reminder-body-param-${i}`}
                  className={inputCls}
                  value={(cfg.reminder_body_params || [])[i] || ""}
                  placeholder={`{{1}} → e.g. {{student_name}}`}
                  onChange={(e) => setParam(i, e.target.value)}
                />
              ))}
            </div>
          )}
          {selectedTpl.button_url_has_var && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Button URL Variable</label>
              <input
                data-testid="reminder-button-param"
                className={inputCls}
                value={cfg.reminder_button_param || ""}
                placeholder="e.g. {{payment_page_link}}"
                onChange={(e) => setCfg((p) => ({ ...p, reminder_button_param: e.target.value }))}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button className={btnPrimary} onClick={save} disabled={saving} data-testid="save-reminder-btn">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Reminder Settings
        </button>
        <button
          type="button"
          onClick={toggleLogs}
          className={`${btnSecondary} flex items-center gap-2`}
          data-testid="toggle-reminder-logs-btn"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${showLogs ? "rotate-180" : ""}`} />
          {showLogs ? "Hide" : "View"} Send Log
        </button>
        {showLogs && (
          <button type="button" onClick={loadLogs} disabled={logsLoading} className={`${btnSecondary} px-2.5`} title="Refresh logs" data-testid="refresh-reminder-logs-btn">
            {logsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        )}
      </div>

      {showLogs && (
        <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="reminder-logs-table">
          {logsLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : logs.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">No reminder records yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">Reg ID</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Phone</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Status</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Template</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Sent / Due</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50" data-testid="reminder-log-row">
                    <td className="px-4 py-2.5 font-mono text-slate-700">{log.registration_id || log.reg_id?.slice(0, 8)}</td>
                    <td className="px-4 py-2.5 text-slate-600">{log.phone}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[log.status] || "bg-slate-100 text-slate-500"}`} data-testid="reminder-status-badge">
                        {log.status}
                      </span>
                      {log.failure_reason && <div className="text-rose-500 mt-0.5">{log.failure_reason}</div>}
                      {log.suppressed_reason && <div className="text-slate-400 mt-0.5">{log.suppressed_reason}</div>}
                      {log.trigger === "manual" && <div className="text-blue-500 mt-0.5" data-testid="reminder-manual-tag">Manual · {log.sent_by}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{log.template_name || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500">
                      {log.sent_at
                        ? new Date(log.sent_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })
                        : log.due_at
                          ? `Due: ${new Date(log.due_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}`
                          : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
}
